import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fleet, Employees } from "../lib/api";
import { Plus, Pencil, Trash2, X, Save, Wrench, ShieldCheck, AlertTriangle, MapPin } from "lucide-react";
import { toast } from "sonner";
import FleetMap from "../components/FleetMap";
import { useConfirm } from "../components/ConfirmProvider";

const EMPTY = {
  brand: "",
  model: "",
  registration: "",
  vin: "",
  year: "",
  notes: "",
  insurance_expiry: "",
  inspection_expiry: "",
  driver_id: "",
  geofence_enabled: false,
  geofence_name: "Baza Szczecin",
  geofence_lat: 53.4285,
  geofence_lng: 14.5528,
  geofence_radius: 50,
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  return Math.round((d - today) / (1000 * 60 * 60 * 24));
}

function ExpiryBadge({ date, label }) {
  if (!date) {
    return (
      <div className="font-mono text-xs text-zinc-400">
        {label}: brak
      </div>
    );
  }
  const days = daysUntil(date);
  let cls = "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (days < 0) cls = "bg-red-100 text-red-800 border-red-300";
  else if (days <= 30) cls = "bg-amber-100 text-amber-800 border-amber-300";
  const formatted = new Date(date).toLocaleDateString("pl-PL");
  return (
    <div className="space-y-1">
      <div className="font-label text-[10px]">{label}</div>
      <div className={`inline-block px-2 py-0.5 border text-xs font-mono ${cls}`}>
        {formatted}
        {days < 0 && " · przeterminowane"}
        {days >= 0 && days <= 30 && ` · za ${days} dni`}
      </div>
    </div>
  );
}

export default function FleetPage() {
  const { confirm } = useConfirm();
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const [data, emps] = await Promise.all([Fleet.list(), Employees.list()]);
      setList(data);
      setEmployees(emps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setForm(EMPTY);
    setEditing({});
  };
  const startEdit = (v) => {
    const gf = v.geofence || {};
    setForm({
      brand: v.brand || "",
      model: v.model || "",
      registration: v.registration || "",
      vin: v.vin || "",
      year: v.year ?? "",
      notes: v.notes || "",
      insurance_expiry: v.insurance_expiry || "",
      inspection_expiry: v.inspection_expiry || "",
      driver_id: v.driver_id || "",
      geofence_enabled: !!v.geofence,
      geofence_name: gf.name || "Baza Szczecin",
      geofence_lat: gf.lat ?? 53.4285,
      geofence_lng: gf.lng ?? 14.5528,
      geofence_radius: gf.radius_km ?? 50,
    });
    setEditing(v);
  };
  const cancel = () => setEditing(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.registration) {
      toast.error("Marka i nr rejestracyjny są wymagane");
      return;
    }
    const payload = {
      brand: form.brand,
      model: form.model,
      registration: form.registration,
      vin: form.vin,
      notes: form.notes,
      year: form.year === "" ? null : Number(form.year),
      insurance_expiry: form.insurance_expiry || null,
      inspection_expiry: form.inspection_expiry || null,
      driver_id: form.driver_id || null,
      geofence: form.geofence_enabled
        ? {
            name: form.geofence_name,
            lat: Number(form.geofence_lat),
            lng: Number(form.geofence_lng),
            radius_km: Number(form.geofence_radius),
          }
        : null,
    };
    try {
      if (editing && editing.id) {
        await Fleet.update(editing.id, payload);
        toast.success("Pojazd zaktualizowany");
      } else {
        await Fleet.create(payload);
        toast.success("Pojazd dodany");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  const remove = async (id) => {
    if (!(await confirm("Usunąć ten pojazd z floty?", { tone: "danger", confirmLabel: "Usuń" }))) return;
    try {
      await Fleet.remove(id);
      toast.success("Pojazd usunięty");
      load();
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-8" data-testid="fleet-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-label mb-1">// Baza pojazdów</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
            Flota
          </h1>
          <p className="text-zinc-600 mt-2 text-sm">
            Pojazdy służbowe, daty ubezpieczeń i przeglądów, ewidencja napraw.
          </p>
        </div>
        <button
          onClick={startNew}
          data-testid="btn-add-vehicle"
          className="inline-flex items-center gap-2 bg-brand text-white px-5 py-3 font-bold uppercase tracking-wider hover:bg-brand-hover transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nowy Pojazd
        </button>
      </div>

      <FleetMap />

      <div className="border border-zinc-900 overflow-x-auto">
        <table className="w-full" data-testid="fleet-table">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <Th>Pojazd</Th>
              <Th>Nr rejestracyjny</Th>
              <Th>Ubezpieczenie / Przegląd</Th>
              <Th>Naprawy</Th>
              <Th className="text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-zinc-500 text-sm">
                  Ładowanie...
                </td>
              </tr>
            )}
            {!loading && list.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="py-12 text-center text-zinc-500 text-sm"
                  data-testid="empty-fleet"
                >
                  Brak pojazdów. Dodaj pierwszy klikając &bdquo;Nowy Pojazd&rdquo;.
                </td>
              </tr>
            )}
            {list.map((v) => (
              <tr
                key={v.id}
                className="border-t border-zinc-200 hover:bg-zinc-50"
                data-testid={`fleet-row-${v.id}`}
              >
                <td className="py-3 px-4">
                  <div className="font-semibold">
                    {v.brand} {v.model}
                  </div>
                  <div className="text-xs text-zinc-500 font-mono">
                    {v.year || "—"} · VIN {v.vin || "—"}
                  </div>
                  {v.driver_id && (() => {
                    const e = employees.find((x) => x.id === v.driver_id);
                    return e ? (
                      <div className="text-[11px] text-brand font-mono mt-1">
                        Kierowca: {e.first_name} {e.last_name}
                      </div>
                    ) : null;
                  })()}
                </td>
                <td className="py-3 px-4 font-mono text-sm uppercase">
                  {v.registration || "—"}
                </td>
                <td className="py-3 px-4 space-y-2">
                  <ExpiryBadge date={v.insurance_expiry} label="Ubezpieczenie" />
                  <ExpiryBadge date={v.inspection_expiry} label="Przegląd" />
                </td>
                <td className="py-3 px-4 text-sm">
                  <Link
                    to={`/fleet/${v.id}`}
                    data-testid={`open-vehicle-${v.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-brand hover:text-brand"
                  >
                    <Wrench className="w-3 h-3" />
                    {v.repairs?.length || 0} napraw
                  </Link>
                </td>
                <td className="py-3 px-4 text-right whitespace-nowrap">
                  <button
                    onClick={() => startEdit(v)}
                    data-testid={`btn-edit-vehicle-${v.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900 mr-2"
                  >
                    <Pencil className="w-3 h-3" /> Edytuj
                  </button>
                  <button
                    onClick={() => remove(v.id)}
                    data-testid={`btn-delete-vehicle-${v.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 text-red-600 hover:border-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <div
          className="fixed inset-0 z-[1100] bg-black/40 flex items-stretch justify-end"
          onClick={cancel}
          data-testid="vehicle-form-overlay"
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-lg h-full border-l border-zinc-900 p-8 overflow-y-auto"
            data-testid="vehicle-form"
          >
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="font-label">// {editing.id ? "Edycja" : "Nowy"}</div>
                <h2 className="font-heading font-black text-2xl uppercase tracking-tight">
                  {editing.id ? "Edytuj pojazd" : "Dodaj pojazd"}
                </h2>
              </div>
              <button
                type="button"
                onClick={cancel}
                data-testid="btn-close-vehicle-form"
                className="p-2 hover:bg-zinc-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Marka" required>
                <input
                  data-testid="vehicle-input-brand"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  required
                />
              </Field>
              <Field label="Model">
                <input
                  data-testid="vehicle-input-model"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                />
              </Field>
              <Field label="Nr rejestracyjny" required>
                <input
                  data-testid="vehicle-input-registration"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono uppercase focus:outline-none focus:border-brand"
                  value={form.registration}
                  onChange={(e) =>
                    setForm({ ...form, registration: e.target.value.toUpperCase() })
                  }
                  required
                />
              </Field>
              <Field label="Rok produkcji">
                <input
                  data-testid="vehicle-input-year"
                  type="number"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                />
              </Field>
              <Field label="VIN" className="col-span-2">
                <input
                  data-testid="vehicle-input-vin"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono uppercase focus:outline-none focus:border-brand"
                  value={form.vin}
                  maxLength={17}
                  onChange={(e) =>
                    setForm({ ...form, vin: e.target.value.toUpperCase() })
                  }
                />
              </Field>
              <Field label="Ubezpieczenie ważne do">
                <input
                  data-testid="vehicle-input-insurance"
                  type="date"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
                  value={form.insurance_expiry}
                  onChange={(e) =>
                    setForm({ ...form, insurance_expiry: e.target.value })
                  }
                />
              </Field>
              <Field label="Przegląd ważny do">
                <input
                  data-testid="vehicle-input-inspection"
                  type="date"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-brand"
                  value={form.inspection_expiry}
                  onChange={(e) =>
                    setForm({ ...form, inspection_expiry: e.target.value })
                  }
                />
              </Field>
              <Field label="Przypisany kierowca" className="col-span-2">
                <select
                  data-testid="vehicle-input-driver"
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
                  value={form.driver_id}
                  onChange={(e) => setForm({ ...form, driver_id: e.target.value })}
                >
                  <option value="">— brak —</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                      {emp.position ? ` · ${emp.position}` : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Uwagi" className="col-span-2">
                <textarea
                  data-testid="vehicle-input-notes"
                  rows={3}
                  className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </Field>

              {/* Geofence section */}
              <div className="col-span-2 mt-2 border-t border-zinc-200 pt-5">
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    data-testid="vehicle-geofence-enabled"
                    className="w-4 h-4 accent-brand"
                    checked={form.geofence_enabled}
                    onChange={(e) =>
                      setForm({ ...form, geofence_enabled: e.target.checked })
                    }
                  />
                  <span className="font-label flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> Strefa pojazdu (alert wyjazdu)
                  </span>
                </label>
                {form.geofence_enabled && (
                  <div className="grid grid-cols-2 gap-3 pl-7">
                    <Field label="Nazwa strefy" className="col-span-2">
                      <input
                        data-testid="vehicle-geofence-name"
                        className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-brand"
                        value={form.geofence_name}
                        onChange={(e) =>
                          setForm({ ...form, geofence_name: e.target.value })
                        }
                      />
                    </Field>
                    <Field label="Szerokość (lat)">
                      <input
                        data-testid="vehicle-geofence-lat"
                        type="number"
                        step="0.0001"
                        className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono text-sm focus:outline-none focus:border-brand"
                        value={form.geofence_lat}
                        onChange={(e) =>
                          setForm({ ...form, geofence_lat: e.target.value })
                        }
                      />
                    </Field>
                    <Field label="Długość (lng)">
                      <input
                        data-testid="vehicle-geofence-lng"
                        type="number"
                        step="0.0001"
                        className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono text-sm focus:outline-none focus:border-brand"
                        value={form.geofence_lng}
                        onChange={(e) =>
                          setForm({ ...form, geofence_lng: e.target.value })
                        }
                      />
                    </Field>
                    <Field label="Promień (km)" className="col-span-2">
                      <input
                        data-testid="vehicle-geofence-radius"
                        type="number"
                        min="1"
                        className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono text-sm focus:outline-none focus:border-brand"
                        value={form.geofence_radius}
                        onChange={(e) =>
                          setForm({ ...form, geofence_radius: e.target.value })
                        }
                      />
                    </Field>
                    <p className="col-span-2 text-[11px] text-zinc-500 font-mono leading-relaxed">
                      Alert zostanie wygenerowany gdy auto wyjedzie poza promień od podanego punktu (lub poza granice Polski — zawsze aktywne).
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-10 flex gap-3">
              <button
                type="submit"
                data-testid="btn-save-vehicle"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-brand text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-brand-hover transition-colors"
              >
                <Save className="w-4 h-4" />
                Zapisz
              </button>
              <button
                type="button"
                onClick={cancel}
                className="px-6 py-3 font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
              >
                Anuluj
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`py-3 px-4 text-xs uppercase tracking-[0.2em] font-bold ${className}`}>
      {children}
    </th>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-label block mb-2">
        {label}
        {required && <span className="text-brand ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
